import * as React from "react";
import ClockLoader from "react-spinners/ClockLoader";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { Box, Button } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

// import IntPhoneNumberField from "../../helper/Form/IntPhoneNumber";
import Alerts from "../../helper/Alert";
import Axios from "axios";
import PhoneOrTelephoneField from "../../helper/Form/PhoneOrTelephoneField";

const schema = yup.object().shape({
  referralName: yup
    .string()
    .required("Referral Name is required")
    .matches(
      /^[A-Za-z][A-Za-z.\s]*$/,
      "Must start with a letter and only contain letters, dots, and spaces"
    )
    .max(100, " cannot exceed 100 characters")
    .test(
      "not-all-same-characters",
      "Name cannot have all characters the same",
      (value) => {
        if (!value) return true; // skip if empty, 'required' already handles this
        const lettersOnly = value.replace(/[^A-Za-z]/g, "").toLowerCase();
        return !/^([a-z])\1+$/.test(lettersOnly); // blocks ffff, FFFF, etc.
      }
    ),
  referralEmail: yup
    .string()
    .notRequired()
    .test(
      "no-spaces",
      "Email must not contain spaces",
      (value) => !value || !/\s/.test(value)
    )
    .test(
      "valid-local-part",
      "Invalid email format: special characters cannot start/end or repeat in local part",
      (value) => {
        if (!value) return true; // Skip validation if empty
        const [localPart] = value.split("@");
        if (/^[._-]|[._-]$/.test(localPart)) return false;
        if (/([._-])\1+/.test(localPart)) return false;
        return true;
      }
    )
    .test("no-numeric-start", "Email cannot start with numbers", (value) => {
      if (!value) return true; // Skip validation if empty
      const [localPart] = value.split("@");
      return !/^\d/.test(localPart);
    })
    .test(
      "is-email",
      "Invalid email",
      (value) =>
        !value || /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)
    ),
  contactType: yup.string().required("Contact type is required"),
  referralPhone: yup.string().when("contactType", {
    is: "mobile",
    then: (schema) =>
      schema
        .required("Indian Referral Mobile Number is required")
        .test(
          "valid-format-if-present",
          "Indian Referral Mobile Number must be in the format +91 XXXXX XXXXX and start with 6-9",
          (value) => {
            if (!value || value.trim() === "") return false;
            const formatRegex = /^\+91 [6-9]\d{4} \d{5}$/;
            return formatRegex.test(value);
          }
        )
        .test(
          "not-all-zeros",
          "Indian Referral Mobile Number cannot contain all zeros",
          (value) => {
            if (!value || value.trim() === "") return false;
            const digits = value.replace(/\D/g, "");
            return digits !== "910000000000";
          }
        ),
  }),
  referralTelephone: yup.string().when("contactType", {
    is: "telephone",
    then: (schema) =>
      schema
        .required("Indian Referral Telephone Number is required")
        .test(
          "valid-format-if-present",
          "Indian Telephone must be in the format +91 XX XXX XXXX",
          (value) => {
            if (!value || value.trim() === "") return false;
            const formatRegex = /^\+91 \d{2} \d{4} \d{4}$/;
            return formatRegex.test(value);
          }
        )
        .test(
          "valid-area-code",
          "Area code must be valid (not all zeros)",
          (value) => {
            if (!value || value.trim() === "") return false;
            const areaCode = value.split(" ")[1];
            return areaCode !== "000";
          }
        ),
  }),
  referralRelationship: yup
    .string()
    .required("Relationship with Referral is required")
    .matches(
      /^[A-Za-z][A-Za-z.\s]*$/,
      "Must start with a letter and only contain letters, dots, and spaces"
    )
    .max(100, " cannot exceed 100 characters")
    .test(
      "not-all-same-characters",
      "Relationship with Referral cannot have all characters the same",
      (value) => {
        if (!value) return true; // skip if empty, 'required' already handles this
        const lettersOnly = value.replace(/[^A-Za-z]/g, "").toLowerCase();
        return !/^([a-z])\1+$/.test(lettersOnly); // blocks ffff, FFFF, etc.
      }
    ),
});

export default function Step3({
  formData,
  setFormData,
  handleNext,
  handleBack,
  applicantId,
  qrOwnerId,
}) {
  const {
    handleSubmit,
    control,
    // setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: formData,
  });

  React.useEffect(() => {
    reset({
      referralName: formData?.referralName || "",
      referralEmail: formData?.referralEmail || "",
      contactType: formData?.contactType || "mobile",
      referralPhone: formData?.referralPhone || "",
      referralTelephone: formData?.referralTelephone || "",
      referralRelationship: formData?.referralRelationship || "",
    });
  }, [formData, reset]);

  const API_URL = process.env.React_App_Api;
  const [alert, setAlert] = React.useState({
    open: false,
    message: "",
    severity: "",
  });

  // React.useEffect(() => {
  //   setValue("contactType", "telephone");
  // }, [formData]);

  const [loading, setLoading] = React.useState(false);

  const handleCloseAlert = () => {
    setAlert({
      open: false,
      message: "",
      severity: "",
    });
  };

  const onSubmit = async (data) => {
    console.log("DATA", data);
    const payload = {
      ivname: data.referralName,
      contactType: data.contactType,
      ivemail: data.referralEmail,
      relationtoapplicant: data.referralRelationship,
      usvname: formData.volunteerName,
      usvphone: formData.volunteerPhone,
      usvemail: formData.volunteerEmail,
      qrOwnerId: qrOwnerId || null,
    };
    if (data.contactType === "mobile") {
      payload.ivphone = data.referralPhone
      payload.ivTelephone = null;
    } else if (data.contactType === "telephone") {
      payload.ivTelephone = data.referralTelephone
      payload.ivphone = null;
    }


    const prevDataString = localStorage.getItem("volunteersDetail");
    const prevData = prevDataString ? JSON.parse(prevDataString)?.volunteersDetail : null;

    // Construct only the changed fields
    let updatedFields = {};
    if (applicantId && prevData) {
      Object.entries(payload).forEach(([key, value]) => {
        if (prevData[key] !== value) {
          updatedFields[key] = value;
        }
      });
    }

    try {
      setLoading(true);
      if (!applicantId) {
        await Axios.post(`${API_URL}/aid/volunteerinsert`, payload).then(
          (res) => {
            setAlert({
              open: true,
              message: 'Reference details Saved successfully',
              severity: "success",
              anchorOrigin: { vertical: "top", horizontal: "center" },
            });
            setTimeout(() => {
              handleNext();
            }, 1000);
            const myData = {
              volunteersDetail: {
                id: res.data._id,
                ivname: res.data.indiavolunteer.ivname,
                // ivphone: res.data.indiavolunteer.ivphone,
                ...(res.data.indiavolunteer.ivphone && { ivphone: res.data.indiavolunteer.ivphone }),
                ...(res.data.indiavolunteer.ivTelephone && { ivTelephone: res.data.indiavolunteer.ivTelephone }),
                ivemail: res.data.indiavolunteer.ivemail,
                relationtoapplicant: res.data.indiavolunteer.relationtoapplicant,
                usvname: res.data.usavolunteer.usvname,
                usvphone: res.data.usavolunteer.usvphone,
                usvemail: res.data.usavolunteer.email,
              },
            };
            const storeVolunteersDetail = JSON.stringify(myData);
            localStorage.setItem("volunteersDetail", storeVolunteersDetail);
          }
        );
      } else {
        if (Object.keys(updatedFields).length > 0) {
          await Axios.patch(`${API_URL}/aid/volunteerupdate/${applicantId}`, updatedFields);
          const prevDataString = localStorage.getItem("volunteersDetail");
          const prevData = prevDataString ? JSON.parse(prevDataString)?.volunteersDetail : {};
          console.log("UPDATED FIELDS", updatedFields);
          const updatedLocalStorage = {
            ...prevData,
            ...updatedFields,
          };

          localStorage.setItem("volunteersDetail", JSON.stringify({ volunteersDetail: updatedLocalStorage }));
          setAlert({
            open: true,
            message: 'Reference details updated successfully',
            severity: "success",
            anchorOrigin: { vertical: "top", horizontal: "center" },
          });
          handleNext();
        } else {
          setAlert({
            open: true,
            message: 'No Data changed',
            severity: "success",
            anchorOrigin: { vertical: "top", horizontal: "center" },
          });
          setTimeout(() => {
            handleNext();
          }, 2000);
        }
      }
    } catch (error) {
      if (error.response?.data?.statusCode === 400) {
        setAlert({
          open: true,
          message: error.response.data?.error,
          severity: "error",
          anchorOrigin: { vertical: "top", horizontal: "center" },
        });
        // Reload the app after displaying the alert
        setTimeout(() => {
          window.location.reload();
        }, 3000); // Adjust the timeout duration as needed
      } else if (error.response?.status === 503) {
        setAlert({
          open: true,
          message: "Database is down. Please try again later.",
          severity: "error",
          anchorOrigin: { vertical: "top", horizontal: "center" },
        });
        // Reload the app after displaying the alert
        setTimeout(() => {
          window.location.reload();
        }, 3000); // Adjust the timeout duration as needed
      } else if (error.message) {
        setAlert({
          open: true,
          message: error.message || "Server Error.Please try again later",
          severity: "error",
          anchorOrigin: { vertical: "top", horizontal: "center" },
        });
        // Reload the app after displaying the alert
        setTimeout(() => {
          window.location.reload();
        }, 3000); // Adjust the timeout duration as needed
      } else {
        // Handle other errors
        setAlert({
          open: true,
          message: "An unexpected error occurred. Please try again later.",
          severity: "error",
          anchorOrigin: { vertical: "top", horizontal: "center" },
        });
        // Reload the app after displaying the alert
        setTimeout(() => {
          window.location.reload();
        }, 3000); // Adjust the timeout duration as needed
      }
    } finally {
      setLoading(false); // Set loading state to false when form submission completes (whether success or error)
    }
    setFormData((prevData) => ({
      ...prevData,
      ...data,
    }));
  };

  return (
    <>
      <Alerts
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <ClockLoader
            color="#1976d2"
            loading={loading}
            size={150}
            aria-label="Loading Spinner"
            data-testid="loader"
          />
        </div>
      ) : (
        <>
          <Grid container spacing={2} p={2} sx={{ display: "flex" }}>
            <Grid item md={3.5} sm={12}>
              <Controller
                name="referralName"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    id="outlined-textarea"
                    label={
                      <label>
                        Indian Referral Name{" "}
                        <span style={{ color: "orangered" }}> *</span>
                      </label>
                    }
                    placeholder="Enter Indian Referral Name"
                    size="small"
                    className="responsive-form"
                    error={!!error}
                    helperText={error ? error.message : ""}
                    sx={{ width: 280, maxWidth: "100%" }}
                  />
                )}
              />
            </Grid>
            <Grid item md={3.5} sm={12}>
              <Controller
                name="referralEmail"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    id="outlined-textarea"
                    label={<label>Indian Referral Email </label>}
                    placeholder="Enter Indian Referral Email"
                    size="small"
                    className="responsive-form"
                    error={!!error}
                    helperText={error ? error.message : ""}
                    sx={{ width: 280, maxWidth: "100%" }}
                  />
                )}
              />
            </Grid>
            <Grid item md={5} sm={12}>
              {/* <IntPhoneNumberField
                control={control}
                name="referralPhone"
                label={
                  <label>
                    Indian Referral Mobile Number
                    <span style={{ color: "orangered" }}>*</span>
                  </label>
                }
                error={errors.referralPhone}
              />
              // In your Step3 component, replace the phone number field with: */}
              <PhoneOrTelephoneField
                control={control}
                name="contactType"
                label="Contact Type *"
                error={{
                  phone: errors.referralPhone,
                  telephone: errors.referralTelephone,
                }}
                phoneName="referralPhone"
                telephoneName="referralTelephone"
                type={formData?.contactType}
              />
            </Grid>
            <Grid item md={4} sm={12}>
              <Controller
                name="referralRelationship"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    id="outlined-textarea"
                    label={
                      <label>
                        Relationship with Referral{" "}
                        <span style={{ color: "orangered" }}> *</span>
                      </label>
                    }
                    placeholder="Enter Relationship with Referral"
                    size="small"
                    className="responsive-form"
                    error={!!error}
                    helperText={error ? error.message : ""}
                    sx={{ width: 280, maxWidth: "100%" }}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Box sx={{ display: "flex" }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              sx={{ mt: 1, mr: 1 }}
            >
              Back
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              variant="contained"
              sx={{ mt: 1, mr: 1 }}
            >
              Continue
            </Button>
          </Box>
        </>
      )}
    </>
  );
}
